// Ô địa chỉ dùng chung (tạo đơn, sửa đơn, sổ địa chỉ khách, kho): công tắc "Địa chỉ cũ / Địa chỉ mới",
// ô gõ / dán cả địa chỉ (gợi ý khu vực khi gõ — suggestAreas; dán thì tự tách), ô Khu vực, ô số nhà / tên đường. Khi người bán chọn / dán / bấm [đổi], giá trị trả ra là địa
// chỉ CŨ — BEST chia đơn theo địa chỉ cũ. Giá trị truyền vào ở dạng mới (ghim bản đồ, dữ liệu cũ) chỉ
// được HIỂN THỊ ở cả hai chế độ, không tự sửa: mở "Sửa đơn" không được tự gửi sửa địa chỉ sang BEST;
// server đổi lúc gửi (docs/KE_HOACH_DIA_CHI_CU_MOI.md).
// Phường mới gộp nhiều phường cũ: phường cũ hệ thống tự chọn chỉ là đoán → hỏi máy chủ (/geo/locate-old: ghim bản
// đồ, không thì số nhà + đường) phường cũ nào; chưa ra thì người bán chạm chọn (phường đường đi qua xếp đầu).

import { useDeferredValue, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { MapPin, TriangleAlert } from 'lucide-react'
import type { AddressMode, AreaIssue, AreaSuggestion, ClassifiedArea, NewArea, OldArea } from '@shoptrack/common/admin-areas'
import { get } from '@/lib/api'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Segmented } from '@/components/ui/segmented'
import { useAdminAreas } from '@/hooks/useAdminAreas'
import { AreaPicker, areaLabel, type PickedArea } from './AreaPicker'

export interface AddressValue {
  street: string
  city: string
  district: string | null
  ward: string
}

const MODE_KEY = 'st_address_mode'
const MODES = [
  ['old', 'Địa chỉ cũ'],
  ['new', 'Địa chỉ mới'],
] as const

function readMode(): AddressMode {
  try {
    return localStorage.getItem(MODE_KEY) === 'new' ? 'new' : 'old'
  } catch {
    return 'old'
  }
}

export function AddressInput({
  value,
  onChange,
  disabled,
  idPrefix = 'address',
  label = 'Địa chỉ *',
  streetPlaceholder = 'VD: 12 Đồng Khởi',
  streetError,
  areaError,
  onIssueChange,
  resolveNew,
  coords,
}: {
  value: AddressValue
  onChange: (next: AddressValue) => void
  disabled?: boolean
  idPrefix?: string
  /** null = không hiện nhãn (nơi gọi đã có nhãn riêng) */
  label?: ReactNode
  streetPlaceholder?: string
  streetError?: string
  areaError?: string
  /** Khu vực chưa đủ chắc để gửi BEST (không nhận ra / chưa chọn phường cũ) — form cha chặn tạo đơn */
  onIssueChange?: (issue: AreaIssue | null) => void
  /** Giá trị đang kiểu mới (ghim bản đồ, địa chỉ đã lưu) cũng tìm / hỏi phường cũ — tạo đơn, kho. Sửa đơn: không,
   *  mở đơn cũ không được tự đổi địa chỉ. */
  resolveNew?: boolean
  /** Toạ độ ghim (nếu có) — tìm phường cũ theo ranh giới trước, rồi mới tới số nhà */
  coords?: { lat: number; lng: number } | null
}) {
  const { data: areas, isError: areasFailed } = useAdminAreas()
  const [mode, setModeState] = useState<AddressMode>(readMode)
  /** Phường mới người bán vừa chọn — giữ đúng phường đó khi phường cũ thuộc nhiều phường mới */
  const [chosenNew, setChosenNew] = useState<NewArea | null>(null)
  const [showCandidates, setShowCandidates] = useState(false)
  /** Phường cũ vừa do ô này tự chọn thay người bán (từ phường mới) — phường trải nhiều quận thì phải chọn lại */
  const [autoPicked, setAutoPicked] = useState(false)
  /** Phường cũ con đường đi qua (máy chủ chưa chắc) — xếp đầu danh sách chọn */
  const [likely, setLikely] = useState<OldArea[]>([])
  const [locating, setLocating] = useState(false)
  /** Khu vực ô này vừa trả ra — giá trị đổi từ bên ngoài (ghim bản đồ, chọn địa chỉ đã lưu) thì bỏ cờ autoPicked */
  const emittedRef = useRef('')
  const [pasteText, setPasteText] = useState('')
  /** Ô dán: không tách được khu vực từ chữ vừa dán */
  const [pasteFailed, setPasteFailed] = useState(false)
  /** Danh sách gợi ý đang mở + dòng đang chọn bằng phím */
  const [listOpen, setListOpen] = useState(false)
  const [active, setActive] = useState(0)
  const typed = useDeferredValue(pasteText)
  const suggestions = useMemo<AreaSuggestion[]>(
    () => (areas && typed.trim().length >= 4 ? areas.suggestAreas(typed, 6) : []),
    [areas, typed],
  )
  const showList = listOpen && !disabled && suggestions.length > 0 && typed === pasteText

  const setMode = (m: AddressMode) => {
    setModeState(m)
    try {
      localStorage.setItem(MODE_KEY, m)
    } catch {
      // trình duyệt chặn lưu — chỉ mất phần nhớ chế độ
    }
  }

  const areaKey = `${value.city}|${value.district ?? ''}|${value.ward}`
  useEffect(() => {
    if (areaKey === emittedRef.current) return
    emittedRef.current = areaKey
    setAutoPicked(false)
    setLikely([])
  }, [areaKey])

  const view = areas && value.ward.trim() ? areas.areaView(value, chosenNew) : null
  const conv = view?.conversion ?? null
  const issue = areas ? areas.areaIssue(view, { hasWard: Boolean(value.ward.trim()), autoPicked, strictNew: resolveNew }) : null

  // Phường cũ đang là đoán → hỏi máy chủ theo ghim / số nhà; ra một phường thì điền luôn. Chỉ khi người bán vừa
  // chọn khu vực (autoPicked) hoặc nơi gọi cho phép (resolveNew) — sửa đơn không tự đổi địa chỉ đã lưu.
  const guessFor = issue === 'confirm_old_ward' && (autoPicked || resolveNew) && view?.new ? view.new : null
  const street = value.street.trim()
  const lat = coords?.lat ?? null
  const lng = coords?.lng ?? null
  useEffect(() => {
    if (!guessFor || disabled || (street.length < 3 && lat == null)) {
      setLocating(false)
      return
    }
    let alive = true
    setLocating(true)
    const t = setTimeout(async () => {
      try {
        const q = new URLSearchParams({ city: guessFor.city, ward: guessFor.ward })
        if (street.length >= 3) q.set('street', street)
        if (lat != null && lng != null) {
          q.set('lat', String(lat))
          q.set('lng', String(lng))
        }
        const r = await get<{ old: OldArea | null; likely: OldArea[] }>(`/geo/locate-old?${q}`)
        if (!alive) return
        if (r?.old) {
          setChosenNew(guessFor)
          emitChosenOld(r.old)
        } else setLikely(r?.likely ?? [])
      } catch {
        if (alive) setLikely([])
      } finally {
        if (alive) setLocating(false)
      }
    }, 700)
    return () => {
      alive = false
      clearTimeout(t)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- emitChosenOld đọc value mới nhất qua street
  }, [guessFor?.city, guessFor?.ward, street, lat, lng, disabled])

  useEffect(() => {
    onIssueChange?.(issue)
  }, [issue, onIssueChange])

  const emitOld = (old: OldArea, street = value.street) => {
    emittedRef.current = `${old.city}|${old.district}|${old.ward}`
    onChange({ street, city: old.city, district: old.district, ward: old.ward })
  }

  /** Người bán tự chọn phường cũ (bảng chọn, dán địa chỉ cũ, bấm một phường trong danh sách) */
  const emitChosenOld = (old: OldArea, street = value.street) => {
    setAutoPicked(false)
    setShowCandidates(false)
    emitOld(old, street)
  }

  /** Đổi sang địa chỉ cũ rồi trả ra; đặc khu không có phường cũ → trả địa chỉ mới (server gửi nguyên). */
  const emitNew = (nw: NewArea, street = value.street) => {
    setChosenNew(nw)
    const r = areas?.newToOld(nw)
    setAutoPicked(Boolean(r))
    if (r) emitOld(r.pick, street)
    else {
      emittedRef.current = `${nw.city}||${nw.ward}`
      onChange({ street, city: nw.city, district: null, ward: nw.ward })
    }
  }

  function pick(p: PickedArea) {
    if (!areas) return
    setShowCandidates(false)
    if (mode === 'old') {
      setChosenNew(null)
      setAutoPicked(false)
      const a = areas.toOldArea(p).area
      emittedRef.current = `${a.city}|${a.district ?? ''}|${a.ward}`
      onChange({ ...value, ...a })
      return
    }
    const nw = areas.findNewArea(p.city, p.ward)
    if (nw) emitNew(nw)
  }

  /** Điền Khu vực + Số nhà từ địa chỉ đã tách / gợi ý đã chọn, tự chuyển công tắc cũ / mới. */
  function applyParsed(parsed: { street: string; area: ClassifiedArea }) {
    setPasteFailed(false)
    setPasteText('')
    setListOpen(false)
    setShowCandidates(false)
    const street = parsed.street || value.street
    if (parsed.area.kind === 'old') {
      setChosenNew(null)
      setMode('old')
      emitChosenOld(parsed.area.old, street)
    } else {
      setMode('new')
      emitNew(parsed.area.new, street)
    }
  }

  /** Dán / Enter / Tách: tách "số nhà, đường, phường, (quận,) tỉnh"; chưa chắc thì mở danh sách gợi ý để chọn. */
  function splitPasted(text: string) {
    if (!areas || !text.trim()) return
    const parsed = areas.parseAddressText(text)
    if (parsed.area) {
      applyParsed({ street: parsed.street, area: parsed.area })
      return
    }
    const found = areas.suggestAreas(text, 6).length > 0
    setPasteFailed(!found)
    setActive(0)
    setListOpen(found)
  }

  // Không nhận ra (view null) → hiện nguyên chữ đang lưu, kèm dòng nhắc bên dưới
  const rawLabel = areaLabel(value)
  // Đang phải chọn phường cũ: không hiện phường hệ thống đoán (dễ tưởng đã chọn) — hiện phường mới
  const asking = issue === 'confirm_old_ward'
  const shown = asking && view?.new ? view.new : areas?.displayArea(view, mode) ?? null
  const shownLabel = shown ? areaLabel(shown) : rawLabel

  return (
    <div className="space-y-2">
      <div className={cn('flex items-center gap-2', label ? 'justify-between' : 'justify-end')}>
        {label && (
          <span className="text-xs font-medium text-ink">{label}</span>
        )}
        <Segmented label="Kiểu địa chỉ" value={mode} onChange={setMode} options={MODES} />
      </div>

      {!disabled && (
        <div className="relative flex gap-1.5">
          <Input
            id={`${idPrefix}-paste`}
            value={pasteText}
            placeholder="VD: 12 Đồng Khởi, P. Sài Gòn, TP.HCM"
            aria-label="Dán địa chỉ"
            autoComplete="off"
            role="combobox"
            aria-autocomplete="list"
            aria-expanded={showList}
            aria-controls={`${idPrefix}-suggest`}
            aria-activedescendant={showList ? `${idPrefix}-suggest-${active}` : undefined}
            onChange={(e) => {
              setPasteText(e.target.value)
              setPasteFailed(false)
              setActive(0)
              setListOpen(true)
            }}
            onFocus={() => setListOpen(true)}
            onBlur={() => setListOpen(false)}
            onPaste={(e) => {
              const text = e.clipboardData.getData('text')
              if (!text.trim()) return
              e.preventDefault()
              setPasteText(text)
              splitPasted(text)
            }}
            onKeyDown={(e) => {
              if (showList && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
                e.preventDefault()
                const n = suggestions.length
                setActive((i) => (e.key === 'ArrowDown' ? (i + 1) % n : (i - 1 + n) % n))
              } else if (e.key === 'Escape' && showList) {
                e.preventDefault()
                setListOpen(false)
              } else if (e.key === 'Enter') {
                e.preventDefault()
                const picked = showList ? suggestions[active] : undefined
                if (picked) applyParsed(picked)
                else splitPasted(pasteText)
              }
            }}
            className="h-11 flex-1 bg-paper text-xs sm:text-sm desktop:h-9"
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={!areas || !pasteText.trim()}
            onClick={() => splitPasted(pasteText)}
            className="h-11 shrink-0 px-3 text-xs desktop:h-9"
          >
            Tách
          </Button>
          {showList && (
            <ul
              id={`${idPrefix}-suggest`}
              role="listbox"
              aria-label="Gợi ý địa chỉ"
              className="absolute inset-x-0 top-full z-30 mt-1 max-h-80 overflow-y-auto rounded-lg border border-rule bg-raise py-1 shadow-overlay"
            >
              {suggestions.map((sg, i) => (
                <li
                  key={sg.area.kind === 'old' ? `o|${areaLabel(sg.area.old)}` : `n|${areaLabel(sg.area.new)}`}
                  id={`${idPrefix}-suggest-${i}`}
                  role="option"
                  aria-selected={i === active}
                  // Giữ focus ở ô gõ — blur trước click sẽ đóng danh sách
                  onMouseDown={(e) => e.preventDefault()}
                  onMouseEnter={() => setActive(i)}
                  onClick={() => applyParsed(sg)}
                  className={cn(
                    'flex min-h-11 cursor-pointer items-start gap-2 px-3 py-2 desktop:min-h-0',
                    i === active ? 'bg-accent/10' : 'hover:bg-paperSoft',
                  )}
                >
                  <MapPin className={cn('mt-0.5 size-4 shrink-0', i === active ? 'text-accent' : 'text-muted')} aria-hidden />
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm text-ink">{areaLabel(sg.area.kind === 'old' ? sg.area.old : sg.area.new)}</span>
                    {sg.street && <span className="block truncate text-xs text-muted">{sg.street}</span>}
                  </span>
                  <span className="mt-0.5 shrink-0 rounded-full bg-paperSoft px-2 py-0.5 text-[11px] text-inkSoft">
                    {sg.area.kind === 'old' ? 'Địa chỉ cũ' : 'Địa chỉ mới'}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      {pasteFailed && (
        <p className="text-xs text-warn">Không tách được khu vực từ địa chỉ này.</p>
      )}

      <Label htmlFor={`${idPrefix}-area`} className="block text-xs font-medium text-ink">
        Khu vực *
      </Label>

      <AreaPicker
        id={`${idPrefix}-area`}
        areas={areas}
        mode={mode}
        label={shownLabel}
        onPick={pick}
        disabled={disabled}
        invalid={Boolean(areaError)}
        loadFailed={areasFailed}
      />
      {areaError && <p className="text-xs text-danger">{areaError}</p>}

      {view && mode === 'old' && !asking && (view.old ? view.new && (
        <p className="text-xs text-muted">Địa chỉ mới: {areaLabel(view.new)}</p>
      ) : (
        <p className="text-xs text-muted">Không có địa chỉ cũ tương ứng.</p>
      ))}

      {view && mode === 'new' && !asking && (
        <div className="space-y-1.5 text-xs">
          {view.old ? (
            <p className="text-muted">
              BEST giao theo: <span className="text-ink">{areaLabel(view.old)}</span>
              {conv && conv.candidates.length > 1 && !disabled && (
                <button
                  type="button"
                  onClick={() => setShowCandidates((s) => !s)}
                  className="ml-1.5 min-h-9 font-medium text-accent underline-offset-2 hover:underline desktop:min-h-0"
                >
                  [đổi]
                </button>
              )}
            </p>
          ) : (
            <p className="text-muted">Không có địa chỉ cũ tương ứng.</p>
          )}
        </div>
      )}

      {asking && (locating ? (
        <p className="text-xs text-muted">Đang tìm phường cũ…</p>
      ) : (
        <p className="flex items-start gap-1 text-xs text-danger">
          <TriangleAlert className="mt-0.5 size-3.5 shrink-0" />
          Chọn phường cũ để BEST giao đúng.
        </p>
      ))}
      {view && conv && !disabled && (showCandidates || (asking && !locating)) && (
        <div className="flex flex-wrap gap-1.5 text-xs" role="radiogroup" aria-label="Phường cũ gửi hãng">
          {[
            ...likely.filter((l) => conv.candidates.some((c) => areas?.sameOldArea(c, l))),
            ...conv.candidates.filter((c) => !likely.some((l) => areas?.sameOldArea(c, l))),
          ].map((o) => {
            // Chưa chọn thì không tô phường hệ thống tự chọn — tránh hiểu nhầm là đã chọn
            const on = !asking && Boolean(view.old && areas?.sameOldArea(o, view.old))
            return (
              <button
                key={`${o.city}|${o.district}|${o.ward}`}
                type="button"
                role="radio"
                aria-checked={on}
                onClick={() => {
                  // Giá trị đang kiểu mới: nhớ phường mới để ô vẫn hiện đúng phường đó sau khi đổi sang cũ
                  if (view.kind === 'new') setChosenNew(view.new)
                  emitChosenOld(o)
                }}
                className={cn(
                  'min-h-9 rounded-full border px-2.5 desktop:min-h-7',
                  on ? 'border-accent bg-accent/10 text-accent' : 'border-rule text-inkSoft hover:border-accent hover:text-accent',
                )}
              >
                {areaLabel({ ward: o.ward, district: o.district })}
              </button>
            )
          })}
        </div>
      )}

      {issue === 'unrecognized' && (
        <p className="flex items-start gap-1 text-xs text-danger">
          <TriangleAlert className="mt-0.5 size-3.5 shrink-0" />
          Chưa nhận ra khu vực "{rawLabel}" — chọn lại Khu vực.
        </p>
      )}

      <Label htmlFor={`${idPrefix}-street`} className="block pt-1 text-xs font-medium text-ink">
        Số nhà, tên đường *
      </Label>
      <Input
        id={`${idPrefix}-street`}
        value={value.street}
        disabled={disabled}
        placeholder={streetPlaceholder}
        autoComplete="off"
        aria-invalid={streetError ? true : undefined}
        onChange={(e) => onChange({ ...value, street: e.target.value })}
        className="h-11 bg-paper text-xs sm:text-sm desktop:h-9"
      />
      {streetError && <p className="text-xs text-danger">{streetError}</p>}
    </div>
  )
}
